import { useEffect, useRef } from 'react'
import { trpc } from '@/config/trpc'
import { useNavigate, useSearchParams } from 'react-router'
import {
  type AuthState,
  TIKTOK_AUTH_STATE_STORAGE_KEY,
  TIKTOK_CODE_VERIFIER_STORAGE_KEY,
} from '@/lib/auth/tiktok'
import { toast } from 'sonner'
import { safeParseRnd } from '@/lib/auth/oauth-state'
import { providerErrorReason, trackPlatformConnectFailed } from '@/lib/analytics/connectionTracking'

export function AuthTikTokPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { mutateAsync: authorize } = trpc.platform.authorize.useMutation()

  const isMutationRunning = useRef(false)

  // Get params from URL
  const state = searchParams.get('state')
  const code = searchParams.get('code')
  const error = searchParams.get('error')
  const error_description = searchParams.get('error_description')

  useEffect(() => {
    if (isMutationRunning.current) {
      console.debug('Mutation already in progress, skipping duplicate call')
      console.debug('This should only happen in development due to StrictMode')
      return
    }

    isMutationRunning.current = true

    // The state comes back through the URL, so it can be missing or garbage.
    const returnedRnd = state ? safeParseRnd(state) : null
    const storedState = JSON.parse(
      localStorage.getItem(TIKTOK_AUTH_STATE_STORAGE_KEY) || 'null',
    ) as AuthState | null

    if (!returnedRnd || !storedState || storedState.rnd !== returnedRnd) {
      console.error(`States did not match:\n\tstored: ${storedState}\n\treceivedState: ${state}`)
      // Same recovery as the Facebook page: without it the spinner spins forever.
      isMutationRunning.current = false
      localStorage.removeItem(TIKTOK_AUTH_STATE_STORAGE_KEY)
      localStorage.removeItem(TIKTOK_CODE_VERIFIER_STORAGE_KEY)
      // Some providers send `?error=…` without echoing the state back. That is still the
      // user cancelling or the provider failing, not a forged callback.
      const reason = !state && error ? providerErrorReason(error) : 'state_mismatch'
      trackPlatformConnectFailed('tiktok', reason, error ?? undefined)
      if (reason === 'cancelled') toast('TikTok connection cancelled')
      else toast.error('TikTok authentication state mismatch. Please try again.')
      navigate('/settings/social-media')
      return
    }

    // Retrieve code verifier for PKCE
    const codeVerifier = localStorage.getItem(TIKTOK_CODE_VERIFIER_STORAGE_KEY)

    // Clean up the stored state and code verifier after successful validation
    localStorage.removeItem(TIKTOK_AUTH_STATE_STORAGE_KEY)
    localStorage.removeItem(TIKTOK_CODE_VERIFIER_STORAGE_KEY)

    const onSuccess = () => {
      toast.success('TikTok connected successfully')
      navigate(storedState.referrerPath)
    }
    const onError = (errorMessage?: string) => {
      toast.error('Failed to connect TikTok', {
        description: errorMessage,
      })
      navigate(storedState.referrerPath)
    }

    new Promise<void>((resolve, reject) => {
      if (code) {
        if (!codeVerifier) {
          console.error('TikTok code verifier not found in localStorage')
          trackPlatformConnectFailed('tiktok', 'missing_verifier')
          onError('Code verifier missing')
          reject(new Error('Code verifier missing'))
          return
        }

        authorize({ platform: 'tiktok', code, codeVerifier })
          .then(() => {
            onSuccess()
            resolve()
          })
          .catch((err) => {
            console.error('TikTok authorization error:', err)
            trackPlatformConnectFailed('tiktok', 'authorize_failed', err)
            onError(err?.message)
            reject(err)
          })
      } else if (error) {
        console.error(`TikTok authorization error: ${error} - ${error_description}`)
        const reason = providerErrorReason(error)
        trackPlatformConnectFailed('tiktok', reason, error)
        // Backing out of the consent screen is a choice, not a failure worth a red toast.
        if (reason === 'cancelled') {
          toast('TikTok connection cancelled')
          navigate(storedState.referrerPath)
        } else {
          onError(error_description || error)
        }
        resolve()
      } else {
        trackPlatformConnectFailed('tiktok', 'missing_code')
        onError('TikTok did not return an authorization code')
        resolve()
      }
    })
  }, [state, code, error, error_description, authorize, navigate])

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="border-primary/30 border-t-primary h-12 w-12 animate-spin rounded-full border-4"></div>
        <p className="text-muted-foreground">Connecting TikTok...</p>
      </div>
    </div>
  )
}
